import { useState } from "react";
import { Button, Input } from "@chef/components";
import { formatDate } from "@chef/helpers";
import { useEffectOnce } from "@chef/hooks";
import { useAppSelector, useDiscount } from "@chef/state-management/hooks";
import { isEqualStrings } from "@chef/utils/equal";
import { intl } from "./DiscountCodeInput.Intl";

interface DiscountCodeInputProps {
  hideDetails?: boolean;
}

export const DiscountCodeInput = ({
  hideDetails = false,
}: DiscountCodeInputProps) => {
  const {
    ADD,
    EXPIRES,
    PLACEHOLDER,
    REBATE,
    USES_LEFT,
    INVALID_CODE,
    CODE_ACTIVE,
  } = intl;

  const { setRk, isError, isLoading } = useDiscount();

  const signupCoupon = useAppSelector((state) => state.signupRtk.coupon);
  const { couponCode, amount, validEndDate, initialUsage, type } =
    signupCoupon || {};

  const [discountCode, setDiscountCode] = useState("");

  // set discount code to initial signup coupon
  useEffectOnce(() => {
    setDiscountCode(couponCode as string);
  }, !!(!discountCode && couponCode));

  const enteredCouponCodeIsValid = isEqualStrings(discountCode, couponCode);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!discountCode) {
      return;
    }

    setRk({ rk: discountCode, userInitiated: true });
  };

  return (
    <div>
      {!hideDetails && (
        <section className="p-4 mb-4 text-sm bg-grey-3">
          {amount && (
            <div className="flex justify-between text-sm">
              <strong>
                {amount} {type === 2 ? "kr" : "%"} {REBATE}
              </strong>
            </div>
          )}
          {initialUsage && (
            <div>
              {USES_LEFT}
              <strong>{initialUsage}</strong>
            </div>
          )}
          {validEndDate && (
            <div>
              {EXPIRES}
              <strong>{formatDate(validEndDate)}</strong>
            </div>
          )}
        </section>
      )}
      <div className="flex gap-4 md:justify-center md:px-12">
        <Input
          placeholder={PLACEHOLDER}
          value={discountCode}
          name="discountCode"
          id="signup-discount-code"
          className="w-full"
          onChange={({ target: { value = "" } }) => {
            setDiscountCode(value);
          }}
          valid={enteredCouponCodeIsValid}
          helperText={enteredCouponCodeIsValid ? CODE_ACTIVE : undefined}
          error={isError ? INVALID_CODE : undefined}
        />
        <div className="flex flex-shrink-0 mt-1.5">
          <Button
            loading={isLoading}
            className="bg-white"
            tiny
            outlined
            type="button"
            onClick={handleSubmit}
          >
            <strong>{ADD}</strong>
          </Button>
        </div>
      </div>
    </div>
  );
};
